import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";

import ThemedSpinner from "../spinner";
import { cn } from "../../utls/utils";

type ActionButtonTone = "primary" | "secondary" | "danger";

type ActionButtonSize = "sm" | "md" | "lg";

export interface ActionButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: ActionButtonTone;
  size?: ActionButtonSize;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  isLoading?: boolean;
  fullWidth?: boolean;
}

const toneStyles: Record<ActionButtonTone, string> = {
  primary:
    "bg-terracotta-500 border-terracotta-500 text-white hover:bg-terracotta-600 focus-visible:ring-terracotta-300", 
  secondary:
    "bg-cream-100 border-beige-300 text-brown-700 hover:bg-cream-200 focus-visible:ring-beige-300",
  danger:
    "bg-danger border-danger text-white hover:bg-danger/90 focus-visible:ring-danger/40",
};

const sizeStyles: Record<ActionButtonSize, string> = {
  sm: "text-xs px-3 py-1.5",
  md: "text-sm px-4 py-2",
  lg: "text-base px-5 py-2.5",
};

const iconWrapper = "inline-flex h-4 w-4 items-center justify-center";

export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  (
    {
      tone = "primary",
      size = "md",
      leadingIcon,
      trailingIcon,
      isLoading = false,
      disabled,
      fullWidth = false,
      className,
      children,
      type = "button",
      ...props
    },
    ref
  ) => {
    const computedDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-lg border font-medium transition-colors duration-150",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-60",
          fullWidth ? "w-full" : "",
          toneStyles[tone],
          sizeStyles[size],
          className
        )}
        disabled={computedDisabled}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading ? (
          <ThemedSpinner size="sm" />
        ) : (
          <>
            {leadingIcon ? <span className={iconWrapper}>{leadingIcon}</span> : null}
            <span className="truncate">{children}</span>
            {trailingIcon ? <span className={iconWrapper}>{trailingIcon}</span> : null}
          </>
        )}
      </button>
    );
  }
);

ActionButton.displayName = "ActionButton";

export const PrimaryActionButton = forwardRef<
  HTMLButtonElement,
  Omit<ActionButtonProps, "tone">
>((props, ref) => <ActionButton ref={ref} tone="primary" {...props} />);

PrimaryActionButton.displayName = "PrimaryActionButton";

export const SecondaryActionButton = forwardRef<
  HTMLButtonElement,
  Omit<ActionButtonProps, "tone">
>((props, ref) => <ActionButton ref={ref} tone="secondary" {...props} />);

SecondaryActionButton.displayName = "SecondaryActionButton";

export const DangerActionButton = forwardRef<
  HTMLButtonElement,
  Omit<ActionButtonProps, "tone">
>((props, ref) => <ActionButton ref={ref} tone="danger" {...props} />);

DangerActionButton.displayName = "DangerActionButton";

export default ActionButton;
